import { useEffect, useCallback, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';
import type { ThemeTokens } from '../helpers/useThemesApi';
import { useAuth } from '../helpers/useAuth';

const SITE_THEME_CACHE_KEY_PREFIX = 'tasking-site-theme';
const SITE_THEME_ENABLED_KEY_PREFIX = 'tasking-site-theme-enabled';
const APPLIED_VARS_KEY_PREFIX = 'tasking-site-theme-vars';

// Module-level event bus to sync enabled state across all useSiteTheme() instances
const TOGGLE_EVENT = 'tasking-site-theme-toggle';
function dispatchToggle(on: boolean) {
  window.dispatchEvent(new CustomEvent(TOGGLE_EVENT, { detail: on }));
}

function getSiteThemeCacheKey(userId: string): string {
  return `${SITE_THEME_CACHE_KEY_PREFIX}:${userId}`;
}

function getSiteThemeEnabledKey(userId: string): string {
  return `${SITE_THEME_ENABLED_KEY_PREFIX}:${userId}`;
}

function getAppliedVarsKey(userId: string): string {
  return `${APPLIED_VARS_KEY_PREFIX}:${userId}`;
}

/** Detect current light/dark mode */
function getEffectiveMode(): 'light' | 'dark' {
  const rootTheme = document.documentElement.getAttribute('data-theme');
  if (rootTheme === 'light' || rootTheme === 'dark') return rootTheme;
  const bodyTheme = document.body.getAttribute('data-theme');
  if (bodyTheme === 'light' || bodyTheme === 'dark') return bodyTheme;
  if (document.documentElement.classList.contains('light')) return 'light';
  if (document.documentElement.classList.contains('dark')) return 'dark';
  if (document.body.classList.contains('light')) return 'light';
  if (document.body.classList.contains('dark')) return 'dark';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Map theme token color keys to the actual CSS variable names used in global.css */
const COLOR_VAR_MAP: Record<string, string[]> = {
  primary: ['--primary'],
  secondary: ['--secondary'],
  accent: ['--accent'],
  background: ['--background'],
  surface: ['--surface', '--card', '--sidebar', '--popup', '--muted'],
  text: [
    '--foreground',
    '--surface-foreground',
    '--card-foreground',
    '--sidebar-foreground',
    '--popup-foreground',
  ],
  textMuted: ['--muted-foreground'],
  border: ['--border'],
  error: ['--error'],
  success: ['--success'],
  warning: ['--warning'],
  info: ['--info'],
};

/** Bridge app-level CSS vars to Untitled UI semantic tokens */
const PROJECT_TO_UNTITLED_UI_MAP: Record<string, string[]> = {
  '--background': ['--color-bg-primary', '--color-bg-primary_alt'],
  '--foreground': ['--color-fg-primary', '--color-text-primary'],
  '--surface': ['--color-bg-secondary', '--color-bg-secondary_subtle'],
  '--card': ['--color-bg-tertiary'],
  '--sidebar': ['--color-bg-secondary_alt'],
  '--popup': ['--color-bg-primary_hover', '--color-bg-overlay'],
  '--border': ['--color-border-primary', '--color-border-secondary'],
  '--primary': ['--color-bg-brand-solid'],
  '--muted': ['--color-bg-quaternary'],
  '--muted-foreground': ['--color-fg-quaternary', '--color-text-tertiary'],
  '--error': ['--color-error-600', '--color-error-500'],
  '--warning': ['--color-warning-600', '--color-warning-500'],
  '--success': ['--color-success-600', '--color-success-500'],
  '--info': ['--color-blue-600', '--color-blue-500'],
};

const PROJECT_TO_AI_COLLAB_MAP: Record<string, string[]> = {
  '--background': ['--ai-collab-bg'],
  '--surface': ['--ai-collab-surface', '--ai-collab-ai-bg'],
  '--card': ['--ai-collab-surface-elevated'],
  '--border': ['--ai-collab-border', '--ai-collab-ai-border'],
  '--foreground': [
    '--ai-collab-text-primary',
    '--ai-collab-text-secondary',
    '--ai-collab-owner-text',
  ],
  '--muted-foreground': ['--ai-collab-text-muted'],
  '--primary': [
    '--ai-collab-accent',
    '--ai-collab-accent-hover',
    '--ai-collab-owner-bg',
    '--ai-collab-speaking',
    '--ai-collab-activity',
  ],
  '--warning': ['--ai-collab-typing'],
};

function trackAppliedVars(userId: string, vars: string[]) {
  try {
    localStorage.setItem(getAppliedVarsKey(userId), JSON.stringify(vars));
  } catch {}
}

/** Apply a single CSS var to both html and body (body needed because .dark class re-declares vars there) */
function setVar(varName: string, value: string) {
  document.documentElement.style.setProperty(varName, value);
  document.body.style.setProperty(varName, value);
}

/** Apply theme CSS variables to the document using the ACTUAL var names from global.css */
function applyThemeToDocument(userId: string, tokens: ThemeTokens) {
  const appliedVars: string[] = [];

  if (tokens.colors) {
    Object.entries(tokens.colors).forEach(([key, value]) => {
      const cssVars = COLOR_VAR_MAP[key];
      if (cssVars) {
        cssVars.forEach((varName) => {
          setVar(varName, value as string);
          appliedVars.push(varName);

          const bridged = PROJECT_TO_UNTITLED_UI_MAP[varName];
          if (bridged) {
            bridged.forEach((uiVar) => {
              setVar(uiVar, value as string);
              appliedVars.push(uiVar);
            });
          }

          const collabVars = PROJECT_TO_AI_COLLAB_MAP[varName];
          if (collabVars) {
            collabVars.forEach((collabVar) => {
              setVar(collabVar, value as string);
              appliedVars.push(collabVar);
            });
          }
        });
      }
    });
  }
  if (tokens.typography) {
    if (tokens.typography.fontFamily) {
      setVar('--font-family-base', tokens.typography.fontFamily);
      appliedVars.push('--font-family-base');
    }
    if (tokens.typography.headingFontFamily) {
      setVar('--font-family-heading', tokens.typography.headingFontFamily);
      appliedVars.push('--font-family-heading');
    }
    if (tokens.typography.fontWeight) {
      setVar('--font-weight-base', tokens.typography.fontWeight);
      appliedVars.push('--font-weight-base');
    }
    if (tokens.typography.headingFontWeight) {
      setVar('--font-weight-heading', tokens.typography.headingFontWeight);
      appliedVars.push('--font-weight-heading');
    }
    if (tokens.typography.fontStyle) {
      setVar('--font-style-base', tokens.typography.fontStyle);
      appliedVars.push('--font-style-base');
    }
  }
  if (tokens.borderRadius) {
    const radiusMap: Record<string, string> = {
      none: '--radius-none',
      sm: '--radius-sm',
      md: '--radius',
      lg: '--radius-lg',
      full: '--radius-full',
    };
    Object.entries(tokens.borderRadius).forEach(([key, value]) => {
      const varName = radiusMap[key];
      if (varName) {
        setVar(varName, value as string);
        appliedVars.push(varName);
      }
    });
  }
  if (tokens.shadows) {
    const shadowMap: Record<string, string> = {
      sm: '--shadow',
      md: '--shadow-md',
      lg: '--shadow-lg',
    };
    Object.entries(tokens.shadows).forEach(([key, value]) => {
      const varName = shadowMap[key];
      if (varName) {
        setVar(varName, value as string);
        appliedVars.push(varName);
      }
    });
  }

  trackAppliedVars(userId, appliedVars);
}

/** Remove a CSS var from both html and body */
function removeVar(varName: string) {
  document.documentElement.style.removeProperty(varName);
  document.body.style.removeProperty(varName);
}

/** Remove all site theme CSS variables from the document */
function clearThemeFromDocument(userId: string) {
  // Clear tracked vars
  try {
    const tracked = localStorage.getItem(getAppliedVarsKey(userId));
    if (tracked) {
      const vars = JSON.parse(tracked) as string[];
      vars.forEach((v) => removeVar(v));
      localStorage.removeItem(getAppliedVarsKey(userId));
      return;
    }
  } catch {}
  // Fallback: remove all known theme-related vars
  const allVars = Object.values(COLOR_VAR_MAP).flat();
  const untitledVars = Object.values(PROJECT_TO_UNTITLED_UI_MAP).flat();
  const aiCollabVars = Object.values(PROJECT_TO_AI_COLLAB_MAP).flat();
  allVars.push(
    ...untitledVars,
    ...aiCollabVars,
    '--font-family-base',
    '--font-family-heading',
    '--font-weight-base',
    '--font-weight-heading',
    '--font-style-base',
    '--radius-none',
    '--radius-sm',
    '--radius',
    '--radius-lg',
    '--radius-full',
    '--shadow',
    '--shadow-md',
    '--shadow-lg',
  );
  allVars.forEach((v) => removeVar(v));
}

/** Pick the correct tokens for current light/dark mode from cache */
function pickTokensForMode(cacheData: any): ThemeTokens | null {
  const mode = getEffectiveMode();
  if (cacheData.pairedTokens && mode === 'dark') return cacheData.pairedTokens;
  return cacheData.tokens ?? null;
}

function getEnabled(userId: string): boolean {
  try {
    const val = localStorage.getItem(getSiteThemeEnabledKey(userId));
    return val !== 'false'; // default true
  } catch {
    return true;
  }
}

async function fetchSiteTheme() {
  const res = await fetch('/_api/user/site-theme');
  if (!res.ok) return null;
  const data = superjson.parse<{ theme: any; pairedTheme?: any }>(await res.text());
  return { theme: data.theme ?? null, pairedTheme: data.pairedTheme ?? null };
}

async function saveSiteTheme(themeId: string | null) {
  const res = await fetch('/_api/user/site-theme', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify({ themeId }),
  });
  if (!res.ok) {
    const err = superjson.parse<{ error: string }>(await res.text());
    throw new Error(err.error);
  }
  const data = superjson.parse<{ theme: any }>(await res.text());
  return data.theme;
}

/**
 * Hook to manage the user's site-wide theme.
 * - Fetches theme from DB on mount
 * - Applies cached theme from localStorage instantly (no flash)
 * - Provides `applySiteTheme(themeId)` and `clearSiteTheme()` functions
 * - Supports enable/disable toggle (persisted in localStorage)
 */
export function useSiteTheme() {
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const userId = useMemo(
    () => (authState.type === 'authenticated' ? String(authState.user.id) : 'anonymous'),
    [authState],
  );
  const [isReady, setIsReady] = useState(false);
  const [enabled, setEnabledState] = useState(() => getEnabled(userId));

  useEffect(() => {
    setEnabledState(getEnabled(userId));
  }, [userId]);

  // Sync enabled state across all hook instances (ThemesPanel + SiteThemeLoader)
  useEffect(() => {
    const handler = (e: Event) => {
      const on = (e as CustomEvent).detail as boolean;
      setEnabledState(on);
      if (on) {
        try {
          const cached = localStorage.getItem(getSiteThemeCacheKey(userId));
          if (cached) {
            const t = pickTokensForMode(JSON.parse(cached));
            if (t) applyThemeToDocument(userId, t);
          }
        } catch {}
      } else {
        clearThemeFromDocument(userId);
      }
    };
    window.addEventListener(TOGGLE_EVENT, handler);
    return () => window.removeEventListener(TOGGLE_EVENT, handler);
  }, [userId]);

  // Apply cached theme from localStorage immediately (before DB fetch)
  useEffect(() => {
    try {
      const isEnabled = getEnabled(userId);
      if (!isEnabled) {
        setIsReady(true);
        return;
      }
      const cached = localStorage.getItem(getSiteThemeCacheKey(userId));
      if (cached) {
        const t = pickTokensForMode(JSON.parse(cached));
        if (t) applyThemeToDocument(userId, t);
      }
    } catch {
      // ignore
    }
    setIsReady(true);
  }, [userId]);

  // Fetch from DB (returns { theme, pairedTheme })
  const { data: themeData } = useQuery({
    queryKey: ['site-theme', userId],
    queryFn: fetchSiteTheme,
    staleTime: 5 * 60 * 1000,
    enabled: isReady && authState.type === 'authenticated',
  });

  const theme = themeData?.theme ?? null;
  const pairedThemeFromDb = themeData?.pairedTheme ?? null;

  // When DB theme loads, apply it and update cache (only if enabled)
  useEffect(() => {
    if (!theme) return;
    try {
      const tokens = JSON.parse(theme.tokens) as ThemeTokens;
      let pairedTokens: ThemeTokens | undefined;
      if (pairedThemeFromDb?.tokens) {
        try {
          pairedTokens = JSON.parse(pairedThemeFromDb.tokens) as ThemeTokens;
        } catch {}
      }

      if (enabled) {
        const mode = getEffectiveMode();
        if (pairedTokens && mode === 'dark') {
          applyThemeToDocument(userId, pairedTokens);
        } else {
          applyThemeToDocument(userId, tokens);
        }
      }

      localStorage.setItem(
        getSiteThemeCacheKey(userId),
        JSON.stringify({
          themeId: theme.id,
          themeName: theme.name,
          tokens,
          pairedThemeId: theme.pairedThemeId,
          pairedTokens,
        }),
      );
    } catch {
      // ignore parse errors
    }
  }, [theme, pairedThemeFromDb, enabled, userId]);

  // Listen for light/dark mode changes and re-apply correct variant
  useEffect(() => {
    if (!enabled || !theme?.pairedThemeId) return;

    const observer = new MutationObserver(() => {
      // Re-apply based on current mode when html class changes
      try {
        const mode = getEffectiveMode();
        const cached = localStorage.getItem(getSiteThemeCacheKey(userId));
        if (!cached) return;
        const cacheData = JSON.parse(cached);
        if (cacheData.pairedTokens && mode === 'dark') {
          applyThemeToDocument(userId, cacheData.pairedTokens);
        } else if (cacheData.tokens) {
          applyThemeToDocument(userId, cacheData.tokens);
        }
      } catch {}
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
    return () => observer.disconnect();
  }, [enabled, theme?.pairedThemeId, userId]);

  const mutation = useMutation({
    mutationFn: saveSiteTheme,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-theme', userId] });
    },
  });

  const applySiteTheme = useCallback(
    async (themeId: string, tokens: ThemeTokens, themeName: string, pairedTokens?: ThemeTokens) => {
      // Apply correct variant based on current mode
      const mode = getEffectiveMode();
      if (pairedTokens && mode === 'dark') {
        applyThemeToDocument(userId, pairedTokens);
      } else {
        applyThemeToDocument(userId, tokens);
      }
      // Auto-enable when applying
      setEnabledState(true);
      localStorage.setItem(getSiteThemeEnabledKey(userId), 'true');
      localStorage.setItem(
        getSiteThemeCacheKey(userId),
        JSON.stringify({ themeId, themeName, tokens, pairedTokens }),
      );
      // Save to DB
      await mutation.mutateAsync(themeId);
    },
    [mutation.mutateAsync, userId],
  );

  const clearSiteTheme = useCallback(async () => {
    clearThemeFromDocument(userId);
    localStorage.removeItem(getSiteThemeCacheKey(userId));
    await mutation.mutateAsync(null);
  }, [mutation.mutateAsync, userId]);

  const toggleEnabled = useCallback(
    (on: boolean) => {
      setEnabledState(on);
      localStorage.setItem(getSiteThemeEnabledKey(userId), String(on));
      if (on) {
        try {
          const cached = localStorage.getItem(getSiteThemeCacheKey(userId));
          if (cached) {
            const t = pickTokensForMode(JSON.parse(cached));
            if (t) applyThemeToDocument(userId, t);
          }
        } catch {}
      } else {
        clearThemeFromDocument(userId);
      }
      dispatchToggle(on);
    },
    [userId],
  );

  return {
    currentTheme: theme,
    applySiteTheme,
    clearSiteTheme,
    isApplying: mutation.isPending,
    enabled,
    toggleEnabled,
  };
}
